import { Head } from "@inertiajs/react"
import { ExternalLink } from "lucide-react"

import AppLogoIcon from "@/components/app-logo-icon"
import PlatformIcon from "@/components/icons/platform-icon"
import MarkdownPreview from "@/components/stories/markdown-preview"
import { Button } from "@/components/ui/button"
import type { CodeTake, PublicStory, Take, TakeMediaFile } from "@/types"

function isVideoContentType(contentType?: string | null) {
  return contentType?.startsWith("video/") ?? false
}

export default function PublicStoriesShow({ story }: { story: PublicStory }) {
  const appName = import.meta.env.VITE_APP_NAME ?? "Broadside"
  const hasTitle = (story.title?.trim().length ?? 0) > 0
  const title = hasTitle ? story.title! : "Story"

  return (
    <>
      <Head title={title} />

      <div className="bg-background text-foreground min-h-screen">
        <header className="flex items-center gap-3 px-6 py-6">
          <AppLogoIcon className="h-9 w-9" />
          <span className="font-serif text-2xl font-black tracking-tight">
            {appName}
          </span>
        </header>

        <main className="mx-auto flex max-w-2xl flex-col gap-10 px-6 py-10">
          {story.title && (
            <h1 className="font-serif text-3xl font-black tracking-tight sm:text-4xl">
              {story.title}
            </h1>
          )}

          <article className="flex flex-col gap-8">
            {story.long_form ? (
              <MarkdownPreview source={story.body} />
            ) : (
              story.takes.map((take: Take | CodeTake) => {
                const visibleMediaFiles = take.media_files.filter(
                  (mediaFile: TakeMediaFile) => mediaFile.url,
                )

                return (
                  <section
                    key={take.id}
                    className="flex flex-col gap-4 border-b pb-8 last:border-b-0 last:pb-0"
                  >
                    <p className="font-serif leading-relaxed whitespace-pre-wrap">
                      {take.body}
                    </p>

                    {visibleMediaFiles.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {visibleMediaFiles.map((mediaFile: TakeMediaFile) =>
                          isVideoContentType(mediaFile.content_type) ? (
                            <video
                              key={mediaFile.id}
                              src={mediaFile.url!}
                              className="max-h-96 rounded-md object-cover"
                              controls
                              playsInline
                              preload="metadata"
                            />
                          ) : (
                            <img
                              key={mediaFile.id}
                              src={mediaFile.url!}
                              alt={mediaFile.alt_text ?? ""}
                              className="max-h-96 rounded-md object-cover"
                            />
                          ),
                        )}
                      </div>
                    )}
                  </section>
                )
              })
            )}
          </article>

          {story.published_links.length > 0 && (
            <section className="flex flex-col gap-4">
              <h2 className="text-muted-foreground text-xs font-medium tracking-widest uppercase">
                Read &amp; like it on
              </h2>
              <div className="flex flex-wrap gap-3">
                {story.published_links.map((link) => (
                  <Button
                    key={link.platform.id}
                    asChild
                    variant="outline"
                    className="gap-2"
                  >
                    <a
                      href={link.published_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <PlatformIcon
                        platform={link.platform.name}
                        className="h-4 w-4"
                      />
                      {link.platform.label}
                      <ExternalLink
                        className="h-3.5 w-3.5"
                        aria-hidden="true"
                      />
                    </a>
                  </Button>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </>
  )
}
